<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="460" content-class="create-contact-dialog">
    <v-card>
      <v-card-title class="headline">
        {{ contact.id ? $t('header.title.update') : $t('header.title.new') }}
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4 pb-8">
        <v-form ref="form" v-model="isValidForm" lazy-validation>
          <v-text-field v-model="contact.firstName" :rules="nameRules" type="text" :label="$t('form.firstName')" />
          <v-text-field v-model="contact.lastName" :rules="nameRules" type="text" :label="$t('form.lastName')" />
          <v-text-field
            ref="contactEmail"
            v-model="contact.email"
            :rules="emailRules"
            :label="$t('form.email')"
            prepend-inner-icon="mdi-email"
            type="text"
            validate-on-blur
            @focus="isValidContact = true"
          />
          <v-text-field
            ref="contactPhone"
            v-model="contact.phone"
            :rules="phoneRules"
            :label="$t('form.phone')"
            prepend-inner-icon="mdi-phone"
            type="text"
            @focus="isValidContact = true"
          />

          <v-expand-transition>
            <v-alert v-show="!isValidContact" type="info" class="mt-4" dense>
              {{ $t('validations.emailOrPhoneRequired') }}
            </v-alert>
          </v-expand-transition>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">
          {{ $t('buttons.cancel') }}
        </v-btn>
        <v-btn color="primary" depressed :disabled="!isValidContact || !isValidForm" :loading="isLoading" @click="save">
          {{ contact.id ? $t('buttons.update') : $t('buttons.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { CrmService } from '@/services';
import { mapActions } from 'vuex';
import libPhoneNumber from 'libphonenumber-js';
import Contact from '@/classes/Contact';

export default {
  name: 'EcsContactDialog',

  data() {
    return {
      dialog: false,
      isLoading: false,
      contact: {},
      originalContact: {},
      isValidForm: true,
      isValidContact: true,
      nameRules: [name => (name && name.length > 0 ? name.length < 60 : true) || this.$t('validations.nameLength')],
      emailRules: [
        email => (email && email.length > 0 ? this.validEmail.test(email) : true) || this.$t('validations.invalidEmail')
      ],
      phoneRules: [this.isValidPhoneNumber],
      event: {
        open: 'open-contact-dialog',
        updated: 'contact-updated',
        cancelled: 'contact-cancelled'
      }
    };
  },

  computed: {
    validEmail() {
      // eslint-disable-next-line
      return new RegExp(/(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/);
    }
  },

  mounted() {
    this.$root.$on(this.event.open, this.open);
  },

  beforeDestroy() {
    this.$root.$off(this.event.open);
  },

  methods: {
    ...mapActions(['snackbarErrorMessage', 'snackbarSuccessMessage']),

    open({ contact, onSave, onCancel }) {
      this.dialog = true;
      this.contact = contact || {};
      // TODO Temporary fix. For contact creation we need to send "phone". For updating we must use "phone1"
      this.contact.phone = this.contact.phone1;
      this.originalContact = { ...contact };
      this.$once(this.event.updated, contact => onSave(contact));
      this.$once(this.event.cancelled, onCancel);
    },

    close() {
      this.resetForm();
      this.dialog = false;
      this.isLoading = false;
    },

    async save() {
      if (!this.isValidForm) {
        return;
      }

      if (this.isMinimumRequiredInfoValid()) {
        this.isValidContact = true;
      } else {
        return (this.isValidContact = false);
      }

      this.$off(this.event.cancelled);

      if (this.contact.id) {
        this.updateFields(this.contact.id).then(contact => {
          this.$emit(this.event.updated, { ...contact });
          this.close();
        });
      } else {
        this.createContact();
      }
    },

    createContact() {
      this.isLoading = true;
      CrmService.createContact({
        email: this.contact.email || undefined,
        phone: this.contact.phone || undefined
      })
        .then(({ data }) => {
          if (!data.success || !data.contactId) {
            this.snackbarErrorMessage(this.$t('messages.createError', { error: data.message }));
            this.isLoading = false;
            return;
          }

          if (this.contact.firstName || this.contact.lastName) {
            this.updateFields(data.contactId, false).finally(() => {
              this.isLoading = false;
              this.$emit(this.event.updated, { ...this.contact });
              this.close();
            });
          } else {
            this.snackbarSuccessMessage(
              this.$t('messages.createSuccess', { name: this.contact.phone || this.contact.email })
            );
            this.isLoading = false;
            this.$emit(this.event.updated, { ...this.contact });
            this.close();
          }
        })
        .catch(error => {
          this.snackbarErrorMessage(this.$t('messages.createError', { error }));
          this.isLoading = false;
        });
    },

    updateFields(contactId, isUpdate = true) {
      this.isLoading = true;
      const contactInfo = {
        firstName: this.contact.firstName || undefined,
        lastName: this.contact.lastName || undefined,
        email: this.contact.email || undefined,
        phone1: this.contact.phone || undefined
      };
      return CrmService.updateContact(contactId, contactInfo)
        .then(() => {
          if (isUpdate) {
            this.snackbarSuccessMessage(
              this.$t('messages.updateSuccess', { name: new Contact(contactInfo).displayName })
            );
          } else {
            this.snackbarSuccessMessage(
              this.$t('messages.createSuccess', { name: new Contact(contactInfo).displayName })
            );
          }
        })
        .catch(() => {
          this.snackbarErrorMessage(this.$t('messages.updateError'));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    isMinimumRequiredInfoValid() {
      return (
        !!(this.contact.email || this.contact.phone) &&
        (this.$refs.contactEmail.validate() || this.$refs.contactPhone.validate())
      );
    },

    isValidPhoneNumber(value = '') {
      if (!value || value.length === 0) {
        return true;
      }

      let phoneNumber;

      try {
        phoneNumber = libPhoneNumber.parsePhoneNumber(value.startsWith('+') ? value : `+1${value}`);
      } catch {
        return this.$t('validations.invalidNumber');
      }

      if (phoneNumber.isValid() && phoneNumber.country !== 'US') {
        return true;
      }

      if (!phoneNumber.isValid()) {
        return this.$t('validations.invalidNumber');
      }

      return true;
    },

    resetForm() {
      this.contact = {};
      this.$refs.form.reset();
      this.isValidContact = true;
    }
  }
};
</script>

<style src="./scoped.scss" scoped lang="scss"></style>
